import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getSubdomain } from '@/lib/subdomain';

export interface SubdomainOrganization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  settings: {
    appearance?: {
      accentColor?: string;
      font?: string;
    };
    [key: string]: unknown;
  } | null;
}

/**
 * Hook to fetch organization data based on the current subdomain
 * 
 * Returns:
 * - organization: The organization data if found
 * - subdomain: The detected subdomain slug
 * - isLoading: Whether the query is in progress
 * - error: Any error that occurred
 * - hasSubdomain: Whether a subdomain was detected
 */
export function useOrganizationFromSubdomain() {
  const subdomain = getSubdomain();
  const hasSubdomain = subdomain !== null;

  const {
    data: organization,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['organization-subdomain', subdomain],
    queryFn: async () => {
      if (!subdomain) {
        return null;
      }

      // Use RPC instead of direct table query so stale/invalid JWTs
      // in the browser cannot trigger auth.users permission errors via RLS.
      const { data, error } = await supabase.rpc('get_organization_by_slug', {
        p_slug: subdomain,
      });

      if (error) throw error;

      if (!data) {
        throw new Error(`Organization "${subdomain}" not found`);
      }

      return data as SubdomainOrganization;
    },
    enabled: hasSubdomain,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
    // Retry on transient/auth failures but not on genuine 404s
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;
      const msg = (error as Error)?.message || '';
      // Don't retry if the org genuinely doesn't exist
      if (msg.includes('not found')) return false;
      // Retry on network/auth/transient errors
      return true;
    },
    retryDelay: 1000,
  });

  return {
    organization,
    subdomain,
    hasSubdomain,
    isLoading: hasSubdomain ? isLoading : false,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Validate if a slug exists in the database
 * Used by the store selector to check before redirecting
 */
export async function validateOrganizationSlug(slug: string): Promise<{
  valid: boolean;
  organization?: SubdomainOrganization;
  error?: string;
}> {
  if (!slug || slug.trim() === '') {
    return { valid: false, error: 'Please enter a store URL' };
  }

  // Normalize the slug
  const normalizedSlug = slug.toLowerCase().trim();

  // Basic validation - only allow alphanumeric and hyphens
  if (!/^[a-z0-9-]+$/.test(normalizedSlug)) {
    return { valid: false, error: 'Store URL can only contain letters, numbers, and hyphens' };
  }

  try {
    // Use a SECURITY DEFINER RPC instead of a direct table query so that stale
    // or invalid JWTs in the browser cannot poison the request with a 401.
    // The function runs as the function owner and only requires the anon apikey.
    const { data, error } = await supabase.rpc('get_organization_by_slug', {
      p_slug: normalizedSlug,
    });

    if (error) throw error;

    if (!data) {
      return { valid: false, error: 'Store not found. Please check the URL and try again.' };
    }

    return { valid: true, organization: data as SubdomainOrganization };
  } catch (err) {
    console.error('Error validating organization slug:', err);
    return { valid: false, error: 'Unable to verify store. Please try again.' };
  }
}

